import { createTheme, MantineProvider, Popover } from "@mantine/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { setLocale } from "yup";

import axiosApi from "~/api/axiosApi";
import { AuthContextProvider } from "~/contexts/AuthContext";
import MainRouter from "~/pages/mainRouter/MainRouter";
import { getAccessToken } from "~/services/tokenService";
import { getCustomValidationErrorMessages } from "~/utils/form/validationErrorMessages";
import { addCustomValidators } from "~/utils/form/validationRules.ts";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			refetchOnMount: true,
			refetchOnReconnect: false,
			retry: false,
			staleTime: 86400000,
		},
	},
});

const App: FunctionComponent = () => {
	const accessToken = getAccessToken();
	axiosApi.defaults.headers.common["Authorization"] = "Bearer " + accessToken;

	useEffect(() => {
		// Initialise custom yup features
		setLocale(getCustomValidationErrorMessages());
		addCustomValidators();
	}, []);


	const theme = createTheme({
		components: {
			Popover: Popover.extend({
				defaultProps: {
					classNames: {
						dropdown: "absolute z-40 bg-white rounded-md shadow-md"
					}
				}
			}),
			InputPlaceholder: {
				defaultProps: {
					classNames: {
						placeholder: "text-gray-400"
					}
				}
			}
		},
	});

	return (
		<QueryClientProvider client={queryClient}>
			<AuthContextProvider accessToken={accessToken}>
				<MantineProvider theme={theme}>
					<MainRouter />
					<ReactQueryDevtools initialIsOpen={false}
										position="left"
										buttonPosition="bottom-left" />
				</MantineProvider>
			</AuthContextProvider>
		</QueryClientProvider>
	);
};

export default App;
